<template>
    <div class="tw-w-full tw-h-full tw-p-4x">
        <!-- Loading State -->
        <div v-if="loading" class="tw-space-y-4">
            <!-- Loading skeleton for company cards -->
            <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4">
                <div v-for="i in 8" :key="i" 
                    class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-sm tw-relative tw-p-5 tw-flex tw-flex-col tw-items-center tw-justify-between tw-min-h-[260px] tw-h-full">
                    <!-- Bookmark skeleton -->
                    <div class="tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-p-1.5 tw-rounded-full tw-bg-gray-200 tw-w-8 tw-h-8 tw-animate-pulse"></div>
                    
                    <!-- Company name skeleton -->
                    <div class="tw-w-full tw-flex tw-flex-col tw-items-start tw-mb-4">
                        <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-w-3/4 tw-animate-pulse tw-mb-2"></div>
                        <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-w-1/2 tw-animate-pulse"></div>
                    </div>

                    <!-- Logo skeleton -->
                    <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-w-full tw-mb-2">
                        <div class="tw-w-32 tw-h-28 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Content -->
        <div v-else>
            <!-- Companies Grid -->
            <div v-if="bookmarkedCompanies.length > 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4">
                <CompanyListCard 
                    v-for="company in bookmarkedCompanies" 
                    :key="company.id" 
                    :company="company" 
                />
            </div>

            <!-- Empty State -->
            <div v-else class="tw-text-center tw-py-12 tw-min-h-[calc(100vh-300px)] tw-flex tw-items-center tw-justify-center">
                <div class="tw-max-w-md tw-mx-auto">
                    <div class="tw-w-24 tw-h-24 tw-mx-auto tw-mb-6 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                        <icon icon="solar:bookmark-outline" class="tw-text-4xl tw-text-gray-400" />
                    </div>
                    <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
                        {{ $t('messages.no-bookmarked-companies', 'No Bookmarked Companies') }}
                    </h3>
                    <p class="tw-text-gray-500 tw-mb-6">
                        {{ $t('messages.no-bookmarked-companies-description', 'You haven\'t bookmarked any companies yet. Start exploring companies and bookmark your favorites!') }}
                    </p>
                    <a-button as="router-link" :to="{ name: 'shared.companies' }" intent="primary" left-icon="solar:buildings-2-bold-duotone">
                        {{ $t('buttons.explore-companies', 'Explore Companies') }}
                    </a-button>
                </div>
            </div>

            <!-- Pagination -->
            <div v-if="bookmarkedCompanies.length > 0" class="tw-mt-8">
                <TablePagination 
                    :has-next="!!paginationOptions.next_page_url" 
                    :has-previous="!!paginationOptions.prev_page_url" 
                    @previous="onPrevious" 
                    @next="onNext" 
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import CompanyListCard from '@/components/companies/CompanyListCard.vue';
import TablePagination from '@/components/table/TablePagination.vue';
import MyEshowApi from '@/api/my-eshow';
import showAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const bookmarkedCompanies = ref([]);
const paginationOptions = ref({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
    next_page_url: null,
    prev_page_url: null,
    first_page_url: null,
    last_page_url: null,
    path: null,
    links: []
});

// Fetch bookmarked companies
const getBookmarkedCompanies = async (page = 1) => {
    try {
        loading.value = true;
        const response = await MyEshowApi.bookmarkedCompanies({ page });
        
        // Update companies data
        bookmarkedCompanies.value = response.data.data || [];
        
        // Update pagination options
        paginationOptions.value = {
            current_page: response.data.current_page || 1,
            last_page: response.data.last_page || 1,
            per_page: response.data.per_page || 10,
            total: response.data.total || 0,
            from: response.data.from || 0,
            to: response.data.to || 0,
            next_page_url: response.data.next_page_url,
            prev_page_url: response.data.prev_page_url,
            first_page_url: response.data.first_page_url,
            last_page_url: response.data.last_page_url,
            path: response.data.path,
            links: response.data.links || []
        };
    } catch (error) {
        console.error('Error fetching bookmarked companies:', error);
        showAlert({
            type: 'error',
            message: t('messages.error-fetching-bookmarked-companies', 'Error fetching bookmarked companies. Please try again.')
        });
    } finally {
        loading.value = false;
    }
};

// Pagination handlers
const onNext = () => {
    if (paginationOptions.value.next_page_url) {
        const nextPage = paginationOptions.value.current_page + 1;
        getBookmarkedCompanies(nextPage);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.prev_page_url) {
        const prevPage = paginationOptions.value.current_page - 1;
        getBookmarkedCompanies(prevPage);
    }
};

// Initialize on mount
onMounted(() => {
    getBookmarkedCompanies();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>